import { createSelector, createSlice } from '@reduxjs/toolkit';
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory } from '../common/reducers/utilities';
import { ensureArray } from '../common/utils/arrayUtilities';

const compareResourceDefs = (r1, r2) => r1.internalid === r2.internalid;
const comapreResources = (r1, r2) => r1.id === r2.id;

const crewResourceSlice = createSlice({
    name: 'crewResource',
    initialState: {
        defs: [],
        data: {},
    },
    reducers: {
        setDefs: (state, action) => {
            state.defs = action.payload
        },
        set: (state, action) => {
            action.payload.forEach(r => delete state.data[r.callsign]);
            action.payload.forEach(r => {
                if (!state.data[r.callsign]) {
                    state.data[r.callsign] = [];
                }
                let idx = state.data[r.callsign].findIndex(r2 => comapreResources(r, r2));
                if (idx >= 0) {
                    state.data[r.callsign].splice(idx, 1, r);
                } else {
                    state.data[r.callsign].push(r);
                }
            })
        },
        addOrUpdateDef: (state, action) => {
            for (const r of ensureArray(action.payload)) {
                let idx = state.defs.findIndex(r2 => compareResourceDefs(r, r2));
                if (idx >= 0) {
                    state.defs.splice(idx, 1, r);
                } else {
                    state.defs.push(r);
                }
            }
        },
        addOrUpdate: (state, action) => {
            for (const r of ensureArray(action.payload)) {
                if (!state.data[r.callsign]) {
                    state.data[r.callsign] = [];
                }
                let idx = state.data[r.callsign].findIndex(r2 => comapreResources(r, r2));
                if (idx >= 0) {
                    state.data[r.callsign].splice(idx, 1, r);
                } else {
                    state.data[r.callsign].push(r);
                }
            }
        },
        removeDef: (state, action) => {
            for (const def of ensureArray(action.payload)) {
                let idx = state.defs.findIndex(r => compareResourceDefs(r, def));
                if (idx >= 0) {
                    state.defs.splice(idx, 1);
                }
            }
        },
        remove: (state, action) => {
            for (const resource of ensureArray(action.payload)) {
                let idx = state.data[resource.callsign]?.findIndex(r => comapreResources(r, resource));
                if (idx >= 0) {
                    state.data[resource.callsign].splice(idx, 1);
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => crewResourceSlice.getInitialState());
    }
})

// #region network operations
const fetchResources = network => network.get('/api/v1/resources', crewResourceSlice.actions.setDefs);
export const insertResource = (network, resource) => network.put('/api/v1/resources', resource, crewResourceSlice.actions.addOrUpdateDef);
export const removeResource = (network, resource) => network.delete('/api/v1/resources/' + encodeURIComponent(resource.internalid), crewResourceSlice.actions.removeDef);

const fetchCrewResources = callsign => network => network.get('/api/v1/crewresources/' + encodeURIComponent(callsign), crewResourceSlice.actions.set);
export const insertCrewResource = (network, resource) => network.post('/api/v1/crewresource', resource, crewResourceSlice.actions.addOrUpdate);
export const updateCrewResource = (network, resource) => network.put('/api/v1/crewresource', resource, crewResourceSlice.actions.addOrUpdate);
export const removeCrewResource = (network, resource) => network.delete('/api/v1/crewresource/' + encodeURIComponent(resource.id), crewResourceSlice.actions.remove);
// #endregion

// #region permission checks
export const canInsertResource = (credentials, resource) => credentials.access.hasFull('administrator');
export const canRemoveResource = (credentials, resource) => credentials.access.hasFull('administrator');
// #endregion

// #region selectors
const selectAllResourceDefs = state => state.crewResource.defs
const selectResourceDefsForSiteLevelType = createSelector([selectAllResourceDefs, (resources, site) => site, (resources, level) => level, (resources, type) => type], (resources, site, level, type) => {
    return resources.filter(r => (!r.site || r.site === site) && (!r.level || r.level === level) && (!r.type || r.type === type));
});
const selectResourceDefs = (site, level, type) => state => selectResourceDefsForSiteLevelType(state, site, level, type);

const selectAllCrewResources = state => state.crewResource.data;
const selectResourceForCrew = createSelector([selectAllCrewResources, (resources, callsign) => callsign], (resources, callsign) => resources[callsign] ?? []);
const selectCrewResources = callsign => state => selectResourceForCrew(state, callsign);
// #endregion

// #region listeners
const listenCrewResourceDef = {
    resourceCreated: (def, { dispatch }) => dispatch(crewResourceSlice.actions.addOrUpdateDef(def)),
    resourceRemoved: (def, { dispatch }) => dispatch(crewResourceSlice.actions.removeDef(def))
}
const listenCrewResource = {
    crewResourceCreated: (resource, { dispatch }) => dispatch(crewResourceSlice.actions.addOrUpdate(resource)),
    crewResourceUpdated: (resource, { dispatch }) => dispatch(crewResourceSlice.actions.addOrUpdate(resource)),
    crewResourceDeleted: (resource, { dispatch }) => dispatch(crewResourceSlice.actions.remove(resource))
}
// #endregion
export const [useAllResourceDefs] = dataHookFactory(selectAllResourceDefs, { run: fetchResources, listen: listenCrewResourceDef });
export const [useResourceDefs] = dataHookFactory(selectResourceDefs, (site, level, type) => ({ run: fetchResources, listen: listenCrewResourceDef }));
export const [useResources] = dataHookFactory(selectCrewResources, initMapFactory(fetchCrewResources, listenCrewResource));

const reducerDetails = { name: crewResourceSlice.name, reducer: crewResourceSlice.reducer };
export default reducerDetails;